/** Speech output via the Web Speech API. Silently unavailable when no voice fits. */

const LANG_MAP: Record<string, string> = {
  DE: 'de-CH',
  FR: 'fr-FR',
  EN: 'en-GB',
  IT: 'it-IT',
  ES: 'es-ES',
  PT: 'pt-PT',
  NL: 'nl-NL',
  LA: 'it-IT',
};

let voices: SpeechSynthesisVoice[] = [];

function refreshVoices(): void {
  if (!('speechSynthesis' in window)) return;
  voices = window.speechSynthesis.getVoices();
}

export function initSpeech(): void {
  if (!('speechSynthesis' in window)) return;
  refreshVoices();
  window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
}

export function toBcp47(languageCode: string): string {
  const key = languageCode.trim().toUpperCase();
  return LANG_MAP[key] ?? key.toLowerCase();
}

function findVoice(bcp47: string): SpeechSynthesisVoice | null {
  if (voices.length === 0) refreshVoices();
  const primary = bcp47.split('-')[0].toLowerCase();

  return (
    voices.find((voice) => voice.lang.toLowerCase() === bcp47.toLowerCase()) ??
    voices.find((voice) => voice.lang.toLowerCase().startsWith(primary)) ??
    null
  );
}

export function canSpeak(languageCode: string): boolean {
  if (!('speechSynthesis' in window)) return false;
  return findVoice(toBcp47(languageCode)) !== null;
}

export function speak(text: string, languageCode: string): void {
  if (!('speechSynthesis' in window)) return;

  const bcp47 = toBcp47(languageCode);
  const voice = findVoice(bcp47);
  if (!voice) return;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.voice = voice;
  utterance.lang = voice.lang;
  // Slightly slower than normal, easier to follow for a learner.
  utterance.rate = 0.85;

  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
}
